/**
 * Session sequence tracking: stores the name of the most recently tracked
 * event so every subsequent event payload includes a `previousEvent` field.
 * Uses sessionStorage so the sequence is tab-scoped and cleared on tab close.
 */
const PREVIOUS_EVENT_KEY = 'pd.analytics.previousEvent';

export const getPreviousEvent = (): string | null => {
  try {
    return sessionStorage.getItem(PREVIOUS_EVENT_KEY) ?? null;
  } catch {
    return null;
  }
};

export const setPreviousEvent = (eventName: string): void => {
  try {
    sessionStorage.setItem(PREVIOUS_EVENT_KEY, eventName);
  } catch {
    // sessionStorage unavailable (e.g., private browsing restrictions)
  }
};

/**
 * Page sequence tracking: `currentPage` is the path of the page being viewed,
 * `previousPage` is the path viewed before it. Track events report `previousPage`
 * directly; page views report `currentPage`, because their own path is about to
 * become the current one.
 */
const CURRENT_PAGE_KEY = 'pd.analytics.currentPage';
const PREVIOUS_PAGE_KEY = 'pd.analytics.previousPage';

export const getCurrentPage = (): string | null => {
  try {
    return sessionStorage.getItem(CURRENT_PAGE_KEY) ?? null;
  } catch {
    return null;
  }
};

export const getPreviousPage = (): string | null => {
  try {
    return sessionStorage.getItem(PREVIOUS_PAGE_KEY) ?? null;
  } catch {
    return null;
  }
};

/**
 * Advances the page pointers. A repeat of the current path (query-string-only
 * change, remount) leaves them untouched so page context does not collapse onto
 * itself.
 */
export const setCurrentPage = (path: string): void => {
  try {
    const current = getCurrentPage();
    if (current === path) {
      return;
    }
    if (current !== null) {
      sessionStorage.setItem(PREVIOUS_PAGE_KEY, current);
    }
    sessionStorage.setItem(CURRENT_PAGE_KEY, path);
  } catch {
    // sessionStorage unavailable (e.g., private browsing restrictions)
  }
};

/**
 * Tracks which providers have already fired OPENED_FULL_MODAL this session.
 * Uses sessionStorage so the set survives SPA navigations and page refreshes
 * but resets when the tab is closed.
 */
const OPENED_MODAL_PROVIDERS_KEY = 'pd.analytics.openedModalProviders';

export const getOpenedModalProviders = (): Set<string> => {
  try {
    const stored = sessionStorage.getItem(OPENED_MODAL_PROVIDERS_KEY);
    return new Set(stored ? (JSON.parse(stored) as string[]) : []);
  } catch {
    return new Set();
  }
};

export const addOpenedModalProvider = (providerId: string): void => {
  try {
    const set = getOpenedModalProviders();
    set.add(providerId);
    sessionStorage.setItem(
      OPENED_MODAL_PROVIDERS_KEY,
      JSON.stringify([...set]),
    );
  } catch {
    // sessionStorage unavailable
  }
};
